import {
  Actionable,
  ActionableType,
  HealthNavigatorAuthorMessage,
  NAVIGATOR_AUTHOR,
  OTHER_SERVICE,
  PatientAuthorMessage,
  PostMessageType
} from '@abiglobalhealth/go-types'
import { MouseEvent, useMemo } from 'react'
import Markdown from 'react-markdown'

import { useConfig } from '@/hooks/useConfig'
import useNavigator from '@/hooks/useNavigator'
import { usePostMessage } from '@/hooks/usePostMessage'

import { RecommendedActionButton } from './RecommendedActionButton'

export const Message = ({ message }: { message: HealthNavigatorAuthorMessage | PatientAuthorMessage }) => {
  const { sendPostMessage } = usePostMessage()
  const { navigator } = useNavigator()
  const { config } = useConfig()

  const allServices = useMemo(() => {
    const availableServices = config?.consultation?.availableServices || []
    if (!availableServices.length) return []
    return availableServices.filter((s) => s !== OTHER_SERVICE.HEALTH_NAVIGATOR)
  }, [config])

  const navigatorMessage = message.sender === NAVIGATOR_AUTHOR.HEALTH_NAVIGATOR ? message : null
  const abiRecommendation = navigatorMessage?.recommendedActions?.find((a) => a.type === ActionableType.ABI_SERVICE)

  const allActions = useMemo<Actionable[]>(() => {
    if (!navigatorMessage) return []
    if (!abiRecommendation) return navigatorMessage.recommendedActions || []
    return [
      abiRecommendation,
      ...allServices
        .filter((s) => s !== abiRecommendation.name)
        .map((s) => ({
          type: ActionableType.ABI_SERVICE,
          name: s,
          source: abiRecommendation.source
        }))
    ]
  }, [navigatorMessage, abiRecommendation, allServices])

  if (navigatorMessage) {
    const { text } = navigatorMessage

    return (
      <div className="flex flex-col gap-6">
        <div className="markdown animate-fade-in list-decimal break-words">
          <Markdown
            components={{
              a: ({ href, children }) => {
                const handleClick = async (e: MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault()
                  await sendPostMessage(PostMessageType.NAVIGATOR_MARKDOWN_LINK_CLICK, {
                    href,
                    children,
                    conversationId: navigator.healthNavigator.conversationId!
                  })
                  if (href) window.location.href = href
                }

                return (
                  <a href={href} target="_self" rel="noreferrer" onClick={handleClick}>
                    {children}
                  </a>
                )
              }
            }}
          >
            {text}
          </Markdown>
        </div>

        {Boolean(allActions?.length) && (
          <div className="flex flex-col gap-4">
            {allActions?.map((action, index) => (
              <RecommendedActionButton key={index} action={action} recommended={(allActions?.length ?? 0) > 1 && index === 0} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return <p className="animate-fade-in break-words whitespace-pre-line">{message.text}</p>
}
